import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuTable } from 'react-icons/lu';

import { HTML_EDITOR_TABLE_MAX_COLS, HTML_EDITOR_TABLE_MAX_ROWS } from './editor-types';

export interface InsertTableMenuProps {
    buttonClassName: string;
    labels: {
        trigger: string;
        menu: string;
        size(_rows: number, _cols: number): string;
    };
    onSelect(_size: { rows: number; cols: number }): void;
}

const DEFAULT_SELECTION = { rows: 1, cols: 1 };

export const InsertTableMenu = (props: InsertTableMenuProps) => {
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState(DEFAULT_SELECTION);
    const [menuStyle, setMenuStyle] = useState<CSSProperties>();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        setSelection(DEFAULT_SELECTION);

        const updatePosition = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                zIndex: 1050
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div className='relative'>
            <button
                id='insert-table-button'
                ref={triggerRef}
                type='button'
                aria-label={props.labels.trigger}
                title={props.labels.trigger}
                aria-expanded={open}
                aria-haspopup='menu'
                onClick={() => setOpen((current) => !current)}
                className={props.buttonClassName}>
                <LuTable className='size-3.5' />
            </button>
            {open
                ? createPortal(
                      <div
                          ref={menuRef}
                          role='menu'
                          aria-label={props.labels.menu}
                          style={menuStyle}
                          className='bg-background-surface outline-border-default flex flex-col gap-2 rounded-md p-2 outline-1 outline-offset-[-1px]'>
                          <div
                              className='grid gap-0.5'
                              style={{ gridTemplateColumns: `repeat(${HTML_EDITOR_TABLE_MAX_COLS}, minmax(0, 1fr))` }}
                              onMouseLeave={() => setSelection(DEFAULT_SELECTION)}>
                              {Array.from({ length: HTML_EDITOR_TABLE_MAX_ROWS }, (_, rowIndex) =>
                                  Array.from({ length: HTML_EDITOR_TABLE_MAX_COLS }, (_, colIndex) => {
                                      const rows = rowIndex + 1;
                                      const cols = colIndex + 1;
                                      const selected = rows <= selection.rows && cols <= selection.cols;
                                      return (
                                          <button
                                              key={`${rows}-${cols}`}
                                              type='button'
                                              role='menuitem'
                                              aria-label={props.labels.size(rows, cols)}
                                              aria-selected={selected}
                                              className={`h-4 w-4 rounded-sm outline outline-1 outline-offset-[-1px] ${selected ? 'bg-brand-primary/25 outline-brand-primary' : 'bg-background-default outline-border-default'}`}
                                              onMouseEnter={() => setSelection({ rows, cols })}
                                              onClick={() => {
                                                  props.onSelect({ rows, cols });
                                                  setOpen(false);
                                              }}
                                          />
                                      );
                                  })
                              )}
                          </div>
                          <div className='text-brand-muted text-center text-xs font-medium'>{props.labels.size(selection.rows, selection.cols)}</div>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};
